import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { FULL_MONTHS, qaStatusColor, classNames, thisMonth, thisYear } from "../lib/utils";
import PerformanceBadge from "../components/PerformanceBadge";
import { Printer, ArrowLeft } from "lucide-react";

export default function PrintableReportPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const home = params.get("home") || (user.access_role === "House Manager" ? user.assigned_home : "ALL");
  const month = +(params.get("month") || thisMonth());
  const year = +(params.get("year") || thisYear());
  const classification = params.get("classification") || "";
  const gender = params.get("gender") || "";
  // Tab that opened the printable. When "qa", the QA Audits section is period-
  // scoped to (month, year) so it matches the on-screen table. When absent or
  // any other tab, keep the historic full-year view so unrelated tabs stay
  // unaffected.
  const tab = params.get("tab") || "";
  const qaMonthScoped = tab === "qa";

  const [staff, setStaff] = useState([]);
  const [summary, setSummary] = useState([]);
  const [qa, setQa] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const sp = {};
      if (home && home !== "ALL") sp.home = home;
      if (classification) sp.classification = classification;
      if (gender) sp.gender = gender;
      const [s1, s2, s3] = await Promise.all([
        api.get("/staff", { params: sp }),
        api.get("/summary", { params: { ...(home && home !== "ALL" ? { home } : {}), month, year } }),
        api.get("/qa/dashboard", { params: { home: home || "ALL", year } }),
      ]);
      let perfRows = s2.data;
      if (classification) {
        perfRows = perfRows.filter(r => r.staff_classification === classification || r.access_role === classification);
      }
      if (gender) perfRows = perfRows.filter(r => r.gender === gender);
      setStaff(s1.data);
      setSummary(perfRows);
      const allQa = s3.data.audits || [];
      setQa(qaMonthScoped ? allQa.filter((a) => Number(a.audit_month) === month) : allQa);
      setLoading(false);
    })();
  }, [home, month, year, classification, gender, qaMonthScoped]);

  const stats = useMemo(() => ({
    headcount: staff.length,
    direct: staff.filter(s => s.staff_classification === "Direct Care Worker").length,
    contract: staff.filter(s => s.staff_classification === "Contract Care Worker").length,
    houseMgr: staff.filter(s => s.access_role === "House Manager").length,
    admin: staff.filter(s => s.access_role === "Administration").length,
    male: staff.filter(s => s.gender === "Male").length,
    female: staff.filter(s => s.gender === "Female").length,
    perfCount: summary.length,
    teamAvg: summary.length ? +(summary.reduce((a, b) => a + b.overall_weighted_score, 0) / summary.length).toFixed(2) : 0,
    directAvg: avgOf(summary.filter(r => r.staff_classification === "Direct Care Worker")),
    contractAvg: avgOf(summary.filter(r => r.staff_classification === "Contract Care Worker")),
    qaAvg: qa.length ? +(qa.reduce((a, b) => a + (b.overall_score_percent || 0), 0) / qa.length).toFixed(1) : 0,
  }), [staff, summary, qa]);

  const filterChips = [];
  if (home && home !== "ALL") filterChips.push(`Home: ${home}`);
  if (classification) filterChips.push(`Classification: ${classification}`);
  if (gender) filterChips.push(`Gender: ${gender}`);

  if (loading) return <div className="p-8 text-slate-500">Building report…</div>;

  // Memoized + non-mutating: .sort() on the original array would mutate summary
  const sortedSummary = [...summary].sort((a, b) => b.overall_weighted_score - a.overall_weighted_score);
  const topThree = sortedSummary.slice(0, 3);
  const needsAttention = summary.filter(r => r.overall_weighted_score < 3.0);

  return (
    <div className="bg-slate-100 min-h-screen">
      <div className="no-print bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <Link to="/reports" className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5"><ArrowLeft size={14} /> Back to Reports</Link>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 bg-sky-800 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-sky-900" data-testid="report-print-btn">
          <Printer size={15} /> Print / Save as PDF
        </button>
      </div>

      <div className="max-w-5xl mx-auto p-8 print-area">
        <div className="bg-white shadow-sm rounded-lg p-10 print:shadow-none print:rounded-none print:p-8 space-y-10">

          {/* Cover */}
          <header className="border-b-2 border-sky-700 pb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src="/nestvillage-logo-transparent.png"
                  alt="NestVillage LLC"
                  className="h-16 w-16 object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div>
                  <p className="font-heading font-bold text-xl text-slate-900">NestVillage LLC</p>
                  <p className="text-xs uppercase tracking-wider font-semibold text-fuchsia-600">Respect · Dignity · Empathy</p>
                  <p className="text-sm text-slate-500 mt-0.5">Care Metrics Platform</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Generated</p>
                <p className="text-sm font-semibold text-slate-700">{new Date().toLocaleDateString("en-US", { dateStyle: "full" })}</p>
                <p className="text-xs text-slate-500 mt-0.5">By {user.full_name}</p>
              </div>
            </div>
            <div className="mt-8">
              <p className="text-xs uppercase tracking-wider font-bold text-sky-700">Operations Report</p>
              <h1 className="font-heading text-4xl font-bold text-slate-900 mt-1">{FULL_MONTHS[month - 1]} {year}</h1>
              <p className="text-lg text-slate-600 mt-1">{home === "ALL" || !home ? "All Homes" : home}</p>
              {filterChips.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {filterChips.map((c) => <span key={c} className="text-xs font-semibold rounded-full bg-slate-100 text-slate-600 border border-slate-200 px-2.5 py-0.5">{c}</span>)}
                </div>
              )}
            </div>
          </header>

          {/* Executive summary */}
          <section>
            <SectionHead n="01" title="Executive Summary" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <KPI label="Active Headcount" value={stats.headcount} />
              <KPI label="Team KPI Average" value={stats.teamAvg.toFixed(2)} sub="/ 5.00" />
              <KPI label="QA Average" value={`${stats.qaAvg}%`} />
              <KPI label="Needs Attention" value={needsAttention.length} sub="< 3.0 KPI" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <KPI label="Direct Care Worker" value={stats.direct} small />
              <KPI label="Contract Care Worker" value={stats.contract} small />
              <KPI label="House Manager" value={stats.houseMgr} small />
              <KPI label="Administration" value={stats.admin} small />
            </div>
            {(stats.directAvg > 0 || stats.contractAvg > 0) && (
              <div className="mt-5 grid grid-cols-2 gap-3">
                <ComparisonCard label="Direct Care Avg" value={stats.directAvg.toFixed(2)} count={summary.filter(r => r.staff_classification === "Direct Care Worker").length} tone="sky" />
                <ComparisonCard label="Contract Care Avg" value={stats.contractAvg.toFixed(2)} count={summary.filter(r => r.staff_classification === "Contract Care Worker").length} tone="purple" />
              </div>
            )}
          </section>

          {/* Top performers */}
          {topThree.length > 0 && (
            <section className="break-inside-avoid">
              <SectionHead n="02" title="Top Performers" />
              <table className="w-full text-sm mt-4 border border-slate-200">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr><th className="px-3 py-2 text-left w-12">#</th><th className="px-3 py-2 text-left">Staff</th><th className="px-3 py-2 text-left">Home</th><th className="px-3 py-2 text-left">Classification</th><th className="px-3 py-2 text-right">Score</th><th className="px-3 py-2 text-left">Band</th></tr>
                </thead>
                <tbody>
                  {topThree.map((r, i) => (
                    <tr key={r.staff_user_id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-bold tabular-nums">{i + 1}</td>
                      <td className="px-3 py-2 font-medium text-slate-800">{r.staff_name}</td>
                      <td className="px-3 py-2 text-slate-600">{r.home}</td>
                      <td className="px-3 py-2 text-slate-600">{r.staff_classification || "—"}</td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums">{r.overall_weighted_score?.toFixed(2)}</td>
                      <td className="px-3 py-2"><PerformanceBadge band={r.performance_band} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Needs attention */}
          {needsAttention.length > 0 && (
            <section className="break-inside-avoid">
              <SectionHead n="03" title="Needs Attention" tone="amber" />
              <table className="w-full text-sm mt-4 border border-slate-200">
                <thead className="bg-amber-50 text-amber-800 text-xs uppercase tracking-wider">
                  <tr><th className="px-3 py-2 text-left">Staff</th><th className="px-3 py-2 text-left">Home</th><th className="px-3 py-2 text-right">Score</th><th className="px-3 py-2 text-left">Recommendation</th></tr>
                </thead>
                <tbody>
                  {needsAttention.map((r) => (
                    <tr key={r.staff_user_id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium text-slate-800">{r.staff_name}</td>
                      <td className="px-3 py-2 text-slate-600">{r.home}</td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums text-amber-700">{r.overall_weighted_score?.toFixed(2)}</td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{r.recommendation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* QA Audits by home */}
          {qa.length > 0 && (
            <section className="break-inside-avoid">
              <SectionHead n={topThree.length ? "04" : "02"} title={qaMonthScoped ? `QA Audits — ${FULL_MONTHS[month - 1]} ${year}` : `QA Audits — ${year}`} />
              <table className="w-full text-sm mt-4 border border-slate-200">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr><th className="px-3 py-2 text-left">Home</th><th className="px-3 py-2 text-left">Period</th><th className="px-3 py-2 text-right">Score</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Completed by</th></tr>
                </thead>
                <tbody>
                  {qa.map((a) => (
                    <tr key={a.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium text-slate-800">{a.home}</td>
                      <td className="px-3 py-2 text-slate-600">{FULL_MONTHS[a.audit_month - 1]} {a.audit_year}</td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums">{a.overall_score_percent}%</td>
                      <td className="px-3 py-2"><span className={classNames("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border", qaStatusColor(a.overall_status))}>{a.overall_status}</span></td>
                      <td className="px-3 py-2 text-slate-600">{a.completed_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Full performance roster */}
          <section className="break-before-page">
            <SectionHead n="05" title="Full Performance Roster" />
            <p className="text-xs text-slate-500 mt-1">{summary.length} staff scored for {FULL_MONTHS[month - 1]} {year}</p>
            <table className="w-full text-xs mt-4 border border-slate-200">
              <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider">
                <tr>
                  <th className="px-2 py-2 text-left">Staff</th>
                  <th className="px-2 py-2 text-left">Home</th>
                  <th className="px-2 py-2 text-left">Classification</th>
                  <th className="px-2 py-2 text-right">Qual</th>
                  <th className="px-2 py-2 text-right">Reli</th>
                  <th className="px-2 py-2 text-right">Doc</th>
                  <th className="px-2 py-2 text-right">H&amp;S</th>
                  <th className="px-2 py-2 text-right">Prof</th>
                  <th className="px-2 py-2 text-right">Comp</th>
                  <th className="px-2 py-2 text-right">Overall</th>
                  <th className="px-2 py-2 text-left">Band</th>
                </tr>
              </thead>
              <tbody>
                {sortedSummary.map((r) => (
                  <tr key={`${r.staff_user_id}-${r.month}-${r.year}`} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 font-medium text-slate-800">{r.staff_name}</td>
                    <td className="px-2 py-1.5 text-slate-600">{r.home}</td>
                    <td className="px-2 py-1.5 text-slate-600">{r.staff_classification || "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.quality_average?.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.reliability_average?.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.documentation_average?.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.health_safety_average?.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.professionalism_average?.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.compliance_average?.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-bold tabular-nums">{r.overall_weighted_score?.toFixed(2)}</td>
                    <td className="px-2 py-1.5"><PerformanceBadge band={r.performance_band} /></td>
                  </tr>
                ))}
                {summary.length === 0 && <tr><td colSpan={11} className="px-3 py-6 text-center text-slate-500">No performance data.</td></tr>}
              </tbody>
            </table>
          </section>

          {/* Footer */}
          <footer className="pt-6 border-t border-slate-200 flex items-center justify-between">
            <p className="text-xs text-slate-400 italic">Confidential — for internal NestVillage LLC use only.</p>
            <p className="text-xs text-slate-400">Page generated {new Date().toLocaleString()}</p>
          </footer>
        </div>
      </div>
    </div>
  );
}

function avgOf(arr) { return arr.length ? +(arr.reduce((a, b) => a + b.overall_weighted_score, 0) / arr.length).toFixed(2) : 0; }

function SectionHead({ n, title, tone = "sky" }) {
  const tones = { sky: "text-sky-700", amber: "text-amber-700" };
  return (
    <div className="flex items-baseline gap-3 border-b border-slate-200 pb-2">
      <span className={classNames("font-heading font-black text-lg tabular-nums", tones[tone])}>{n}</span>
      <h2 className="font-heading font-bold text-2xl text-slate-900">{title}</h2>
    </div>
  );
}

function KPI({ label, value, sub, small }) {
  return (
    <div className={classNames("rounded-lg border border-slate-200 p-3", small && "p-2.5")}>
      <p className="text-[11.5px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
      <div className="mt-1 flex items-baseline gap-1">
        <span className={classNames("font-heading font-bold text-slate-900 tabular-nums", small ? "text-xl" : "text-3xl")}>{value}</span>
        {sub && <span className="text-xs text-slate-400">{sub}</span>}
      </div>
    </div>
  );
}

function ComparisonCard({ label, value, count, tone }) {
  const tones = {
    sky: "border-sky-200 bg-sky-50 text-sky-900",
    purple: "border-purple-200 bg-purple-50 text-purple-900",
  };
  return (
    <div className={classNames("rounded-lg border-2 p-4", tones[tone])}>
      <p className="text-xs uppercase tracking-wider font-bold opacity-70">{label}</p>
      <p className="mt-2 text-4xl font-heading font-bold tabular-nums">{value}</p>
      <p className="text-xs opacity-70 mt-1">{count} staff scored</p>
    </div>
  );
}
